/**
 * TeamLeader Workspace — Student × Course module manager.
 *
 * Reached by clicking a course name on the student profile's Courses tab. Replaces the collapsible
 * "Manage modules" panel that used to live inside each course card: that panel could only complete,
 * and a course with thirty modules is not a list you want inside an accordion inside a card.
 *
 * Two opposite actions over one selection, because a leader working through a student's course is
 * doing one job — deciding, module by module, what counts as done:
 *
 *   • Mark complete  — waves the module through without the student watching it.
 *   • Send to restudy — clears completion AND the watch progress behind it, so it must be watched
 *     again. Destructive in a way "complete" is not, so it is confirmed and styled as such.
 *
 * Both are module-level ONLY. Neither waives the checkpoint/final exam gates and neither issues or
 * revokes a certificate, so a course whose modules are all ticked here still reads "still open until
 * the exam gates are passed" on the profile — see ITeamLeaderService.CompleteStudentModulesAsync.
 */
import { createFileRoute, useNavigate, useParams, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useStudentProfile } from "@/hooks/use-tl-students";
import { useTlStudentExamProfile } from "@/hooks/use-tl-exams";
import {
  fetchStudentCourseModules,
  completeStudentModules,
  restudyStudentModules,
  type StudentModuleProgress,
} from "@/lib/api/tl-students";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  CheckCircle2,
  RotateCcw,
  AlertTriangle,
  PlayCircle,
  FileText,
  Link2,
  CircleDashed,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/team-leader/students_/$studentId_/courses/$courseId",
)({
  component: StudentCourseModulesPage,
});

const ROUTE_ID = "/_authenticated/team-leader/students_/$studentId_/courses/$courseId" as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

function ago(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : formatDistanceToNow(d, { addSuffix: true });
}

function formatDuration(totalSeconds: number): string {
  if (!totalSeconds) return "—";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.round((totalSeconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Module kind → icon, so a resource is distinguishable from a video at a glance. */
function ModuleTypeIcon({ type }: { type: string }) {
  const className = "h-3.5 w-3.5 shrink-0 text-muted-foreground";
  if (type === "Video" || type === "YoutubeLink") return <PlayCircle className={className} />;
  if (type === "ExternalResource") return <Link2 className={className} />;
  return <FileText className={className} />;
}

/** Where the student stands on one module. Mirrors the three UserModuleProgress states. */
function ModuleStatus({ module }: { module: StudentModuleProgress }) {
  if (module.status === "Completed") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Completed {module.completedAt ? ago(module.completedAt) : ""}
      </span>
    );
  }
  if (module.status === "InProgress") {
    return (
      <span className="inline-flex items-center gap-1 text-amber-600">
        <CircleDashed className="h-3.5 w-3.5" />
        {module.progressPercentage}% watched
      </span>
    );
  }
  return <span className="text-muted-foreground">Not started</span>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

function StudentCourseModulesPage() {
  const { studentId, courseId } = useParams({ from: ROUTE_ID });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selected, setSelected] = useState<string[]>([]);
  const [restudyOpen, setRestudyOpen] = useState(false);

  const { data: student } = useStudentProfile(studentId);
  const { data: examProfile } = useTlStudentExamProfile(studentId);

  // The course header is enrichment, not a prerequisite: the module list is its own request and must
  // render even if the profile rollup is still in flight or does not list this course.
  const course = useMemo(
    () => (examProfile?.courseProgress ?? []).find((c) => c.courseId === courseId),
    [examProfile, courseId],
  );

  const {
    data: modules = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tl-student-course-modules", studentId, courseId],
    queryFn: () => fetchStudentCourseModules(studentId, courseId),
    enabled: !!studentId && !!courseId,
  });

  // Which of the CURRENT selection each action would actually change. Both buttons label themselves
  // with these counts rather than with selected.length, so a selection spanning done and undone
  // modules never promises more than the server will do.
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const toComplete = modules.filter((m) => selectedSet.has(m.moduleId) && m.canComplete);
  const toRestudy = modules.filter((m) => selectedSet.has(m.moduleId) && m.canRestudy);

  // Invalidate the module list AND every rollup computed from it — the course cards and progress
  // summaries on the profile, and the roster row's percentage.
  const invalidateProgress = () => {
    setSelected([]);
    qc.invalidateQueries({ queryKey: ["tl-student-course-modules", studentId, courseId] });
    qc.invalidateQueries({ queryKey: ["tl-exams", "student-profile", studentId] });
    qc.invalidateQueries({ queryKey: ["tl-students"] });
  };

  const completeMutation = useMutation({
    mutationFn: (moduleIds: string[]) => completeStudentModules(studentId, moduleIds),
    onSuccess: (result) => {
      // Report what CHANGED, not what was clicked: "5 completed" when two were already done, or one
      // was refused as out of the student's reach, is a false receipt.
      const parts = [`${result.completed} module${result.completed === 1 ? "" : "s"} completed`];
      if (result.alreadyComplete > 0) parts.push(`${result.alreadyComplete} already complete`);
      if (result.skipped.length > 0) parts.push(`${result.skipped.length} skipped`);
      toast.success(parts.join(" · "));
      invalidateProgress();
    },
    onError: (e: Error) => toast.error(e.message || "Failed to complete modules"),
  });

  const restudyMutation = useMutation({
    mutationFn: (moduleIds: string[]) => restudyStudentModules(studentId, moduleIds),
    onSuccess: (result) => {
      const parts = [`${result.reset} module${result.reset === 1 ? "" : "s"} sent back to restudy`];
      if (result.alreadyNotStarted > 0) parts.push(`${result.alreadyNotStarted} already not started`);
      if (result.skipped.length > 0) parts.push(`${result.skipped.length} skipped`);
      toast.success(parts.join(" · "));
      setRestudyOpen(false);
      invalidateProgress();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Failed to send modules back to restudy");
      setRestudyOpen(false);
    },
  });

  const isPending = completeMutation.isPending || restudyMutation.isPending;

  const toggle = (moduleId: string) =>
    setSelected((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId],
    );

  const allSelected = modules.length > 0 && selected.length === modules.length;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* ── Header ── */}
      <div className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs"
          onClick={() =>
            navigate({
              to: "/team-leader/students/$studentId",
              params: { studentId },
            })
          }
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to {student?.fullName ?? "student"}
        </Button>

        <div>
          <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
            {course?.courseTitle ?? "Course modules"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Managing modules for{" "}
            <Link
              to="/team-leader/students/$studentId"
              params={{ studentId }}
              className="font-medium text-foreground underline underline-offset-4"
            >
              {student?.fullName ?? "this student"}
            </Link>
          </p>
        </div>
      </div>

      {/* ── Course rollup ── */}
      {course && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base">Course progress</CardTitle>
                <CardDescription className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <span>{formatDuration(course.totalDurationSeconds)} of content</span>
                  <span>Last activity {ago(course.lastActivityAt)}</span>
                </CardDescription>
              </div>
              {course.isCompleted ? (
                <Badge className="shrink-0 bg-emerald-600 hover:bg-emerald-600">Completed</Badge>
              ) : (
                <Badge variant="outline" className="shrink-0">
                  In progress
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-semibold">{course.completionPercent}%</span>
              <span className="text-xs text-muted-foreground">
                {course.completedModules}/{course.totalModules} modules
              </span>
            </div>
            <Progress value={course.completionPercent} className="h-2" />

            {/* The honest caveat, repeated from the profile: neither action on this page touches the
                exam gates, so 100 % here does not mean the course is complete. */}
            {!course.isCompleted &&
              course.totalModules > 0 &&
              course.completedModules === course.totalModules && (
                <p className="flex items-center gap-1.5 pt-1 text-xs text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  All modules watched — course still open until the exam gates are passed.
                </p>
              )}
          </CardContent>
        </Card>
      )}

      {/* ── Module list ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Modules</CardTitle>
          <CardDescription className="text-xs">
            Select modules, then mark them complete or send them back to be restudied. Neither action
            waives the exam gates or issues a certificate.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Couldn't load this course's modules."}
            </p>
          ) : modules.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No published modules in this course for this student.
            </p>
          ) : (
            <>
              <div className="divide-y rounded-lg border">
                {modules.map((m, idx) => (
                  <label
                    key={m.moduleId}
                    className="flex cursor-pointer items-center gap-3 p-3 text-sm hover:bg-accent/40"
                  >
                    {/* Every module is selectable here, unlike the old complete-only panel: a
                        completed module is exactly the one a restudy is for, so disabling it would
                        put the primary target of one action out of reach. */}
                    <Checkbox
                      checked={selectedSet.has(m.moduleId)}
                      disabled={isPending}
                      onCheckedChange={() => toggle(m.moduleId)}
                      aria-label={`Select ${m.title}`}
                    />
                    <span className="w-6 shrink-0 text-right tabular-nums text-muted-foreground">
                      {idx + 1}.
                    </span>
                    <ModuleTypeIcon type={m.type} />
                    <span className="min-w-0 flex-1 truncate font-medium" title={m.title}>
                      {m.title}
                    </span>
                    <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                      {formatDuration(m.durationSeconds)}
                    </span>
                    <span className="shrink-0 text-xs">
                      <ModuleStatus module={m} />
                    </span>
                  </label>
                ))}
              </div>

              {/* ── Action bar ── */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={isPending}
                    onClick={() =>
                      setSelected(allSelected ? [] : modules.map((m) => m.moduleId))
                    }
                  >
                    {allSelected ? "Clear" : `Select all ${modules.length}`}
                  </Button>
                  <span className="text-xs text-muted-foreground">{selected.length} selected</span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs text-destructive hover:text-destructive"
                    disabled={toRestudy.length === 0 || isPending}
                    onClick={() => setRestudyOpen(true)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {restudyMutation.isPending
                      ? "Resetting…"
                      : `Send ${toRestudy.length || ""} to restudy`.replace("  ", " ")}
                  </Button>

                  <Button
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    disabled={toComplete.length === 0 || isPending}
                    onClick={() => completeMutation.mutate(toComplete.map((m) => m.moduleId))}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {completeMutation.isPending
                      ? "Completing…"
                      : `Mark ${toComplete.length || ""} complete`.replace("  ", " ")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmed because it DESTROYS work: the watch history behind a completion cannot be
          recovered afterwards. One click of friction, not a typed word — this is a routine teaching
          decision, not an irreversible record deletion. */}
      <AlertDialog open={restudyOpen} onOpenChange={(next) => !isPending && setRestudyOpen(next)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send {toRestudy.length} module{toRestudy.length === 1 ? "" : "s"} back to restudy?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  {student?.fullName ?? "This student"} will have to watch{" "}
                  {toRestudy.length === 1 ? "this module" : "these modules"} again from the start.
                  Their completion and their watch progress — position, watched ranges and time spent
                  — are cleared and cannot be restored.
                </p>
                <p className="text-muted-foreground">
                  Certificates already issued and any exam-gate waiver are left as they are.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isPending}
              onClick={(e) => {
                // The dialog closes on its own action click; keep it open until the request
                // settles so the pending state is visible and a failure can say so.
                e.preventDefault();
                restudyMutation.mutate(toRestudy.map((m) => m.moduleId));
              }}
            >
              {restudyMutation.isPending ? "Resetting…" : "Send to restudy"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
